import React, { useMemo, useState } from "react";

import { useEspmPropertyTypeTaxonomy } from "../../../hooks/useEspmPropertyTypeTaxonomy";
import {
  APPLICANT_CLASS_LABELS,
  AUTHORABLE_APPLICANT_CLASS_IDS,
  AUTHORABLE_BUILDING_USE_IDS,
  BUILDING_USE_LABELS,
} from "../../../services/incentiveMatchingVocabulary";
import {
  IncentiveRequirementsVectorFields,
  UTILITY_PROVIDER_OPTIONS,
  mergeIncentiveRequirementsFields,
  parseIncentiveRequirementsFields,
} from "./incentiveRequirementsVector";

interface IProps {
  requirementsJson?: string | null;
  stateCode?: string | null;
  onChange: (nextJson: string) => void;
}

export const IncentiveEligibilityVectorFields: React.FC<IProps> = ({
  requirementsJson,
  stateCode,
  onChange,
}) => {
  const {
    categories,
    loading: taxonomyLoading,
    error: taxonomyError,
  } = useEspmPropertyTypeTaxonomy();
  const fields = useMemo(
    () => parseIncentiveRequirementsFields(requirementsJson),
    [requirementsJson]
  );
  const parseFailed =
    Boolean(requirementsJson?.trim()) && isInvalidJson(requirementsJson);

  const categoryNames = useMemo(
    () => categories.map((category) => category.itemName).filter(Boolean),
    [categories]
  );
  const detailOptions = useMemo(() => {
    const options: { name: string; categoryName: string }[] = [];
    for (const category of categories) {
      for (const detail of category.details ?? []) {
        if (!detail.itemName) {
          continue;
        }
        options.push({
          name: detail.itemName,
          categoryName: category.itemName,
        });
      }
    }
    return options;
  }, [categories]);

  const utilityOptions = useMemo(() => [...UTILITY_PROVIDER_OPTIONS], []);
  const applicantOptions = useMemo(
    () => [...AUTHORABLE_APPLICANT_CLASS_IDS],
    []
  );
  const buildingUseOptions = useMemo(
    () => [...AUTHORABLE_BUILDING_USE_IDS],
    []
  );

  const emit = (next: IncentiveRequirementsVectorFields) => {
    onChange(
      mergeIncentiveRequirementsFields(requirementsJson, next, { stateCode })
    );
  };

  const patch = <K extends keyof IncentiveRequirementsVectorFields>(
    key: K,
    value: IncentiveRequirementsVectorFields[K]
  ) => {
    emit({ ...fields, [key]: value });
  };

  return (
    <div className="col-span-12 space-y-6">
      <div>
        <div className="text-xs font-semibold uppercase text-gray-500">
          Eligibility Vector
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Flat schemaVersion=1 root fields. Required state comes from the
          program State field above. Empty eligible ESPM lists mean every
          property type passes, unless an excluded list matches.
        </p>
      </div>

      {parseFailed && (
        <p className="text-xs text-red-600">
          Existing requirements JSON is invalid, so these fields cannot be
          edited until it is repaired.
        </p>
      )}
      {taxonomyError && (
        <p className="text-xs text-red-600">{taxonomyError}</p>
      )}
      {taxonomyLoading && (
        <p className="text-xs text-gray-500">Loading ESPM property types…</p>
      )}

      <fieldset disabled={parseFailed} className="space-y-6">
        <Section title="Location and size">
          <TriStateSelect
            label="Must be in NYC"
            value={fields.mustBeInNyc}
            onChange={(mustBeInNyc) => patch("mustBeInNyc", mustBeInNyc)}
          />
          <TagListInput
            label="Eligible ZIP codes"
            values={fields.eligibleZipCodes}
            onChange={(eligibleZipCodes) => patch("eligibleZipCodes", eligibleZipCodes)}
            placeholder="Add ZIP and press Enter"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <NumberInput
              label="Minimum units"
              value={fields.minimumUnits}
              onChange={(minimumUnits) => patch("minimumUnits", minimumUnits)}
              integer
            />
            <NumberInput
              label="Maximum units"
              value={fields.maximumUnits}
              onChange={(maximumUnits) => patch("maximumUnits", maximumUnits)}
              integer
            />
            <NumberInput
              label="Minimum sq ft"
              value={fields.minimumSqFt}
              onChange={(minimumSqFt) => patch("minimumSqFt", minimumSqFt)}
              integer
            />
            <NumberInput
              label="Maximum sq ft"
              value={fields.maximumSqFt}
              onChange={(maximumSqFt) => patch("maximumSqFt", maximumSqFt)}
              integer
            />
            <NumberInput
              label="Minimum capacity (MW)"
              value={fields.minimumCapacityMw}
              onChange={(minimumCapacityMw) =>
                patch("minimumCapacityMw", minimumCapacityMw)
              }
            />
          </div>
        </Section>

        <Section title="Utility">
          <NameChecklist
            legend="Utility territories"
            names={utilityOptions}
            selected={fields.utilityTerritories}
            onChange={(utilityTerritories) =>
              patch("utilityTerritories", utilityTerritories)
            }
            columns={3}
            allowCustom
            customLabel="Custom territory"
          />
          <ProviderSelect
            label="Required electric provider"
            value={fields.requiredElectricProvider}
            options={utilityOptions}
            onChange={(requiredElectricProvider) =>
              patch("requiredElectricProvider", requiredElectricProvider)
            }
          />
          <ProviderSelect
            label="Required gas provider"
            value={fields.requiredGasProvider}
            options={utilityOptions}
            onChange={(requiredGasProvider) =>
              patch("requiredGasProvider", requiredGasProvider)
            }
          />
        </Section>

        <Section title="Applicants and typology">
          <p className="text-xs text-gray-500">
            These are the same values a property stores as its owner class, and they
            are matched exactly. A value typed in free text matches no property, so
            this list cannot be added to here.
          </p>
          <NameChecklist
            legend="Eligible applicant classes"
            names={applicantOptions}
            labels={APPLICANT_CLASS_LABELS}
            selected={fields.eligibleApplicantClasses}
            onChange={(eligibleApplicantClasses) =>
              patch("eligibleApplicantClasses", eligibleApplicantClasses)
            }
            columns={3}
            unknownHint="not a known applicant class — matches no property"
          />
          <NameChecklist
            legend="Excluded applicant classes"
            names={applicantOptions}
            labels={APPLICANT_CLASS_LABELS}
            selected={fields.excludedApplicantClasses}
            onChange={(excludedApplicantClasses) =>
              patch("excludedApplicantClasses", excludedApplicantClasses)
            }
            columns={3}
            unknownHint="not a known applicant class — excludes no property"
          />
          <TagListInput
            label="Asset typology"
            values={fields.assetTypology}
            onChange={(assetTypology) => patch("assetTypology", assetTypology)}
            placeholder="Add typology tag and press Enter"
          />
        </Section>

        <Section title="Building use">
          <p className="text-xs text-gray-500">
            These choices use the same direct vocabulary as property intake. Mixed use is a
            standalone choice and does not add another use automatically.
          </p>
          <NameChecklist
            legend="Eligible building uses"
            names={buildingUseOptions}
            labels={BUILDING_USE_LABELS}
            selected={fields.eligibleBuildingUses}
            onChange={(eligibleBuildingUses) =>
              patch("eligibleBuildingUses", eligibleBuildingUses)
            }
            columns={3}
            unknownHint="not offered for incentive requirements"
          />
          <NameChecklist
            legend="Excluded building uses"
            names={buildingUseOptions}
            labels={BUILDING_USE_LABELS}
            selected={fields.excludedBuildingUses}
            onChange={(excludedBuildingUses) =>
              patch("excludedBuildingUses", excludedBuildingUses)
            }
            columns={3}
            unknownHint="not offered for incentive requirements"
          />
        </Section>

        <Section title="Requirement flags">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TriStateSelect
              label="Requires affordable housing"
              value={fields.requiresAffordableHousing}
              onChange={(requiresAffordableHousing) =>
                patch("requiresAffordableHousing", requiresAffordableHousing)
              }
            />
            <TriStateSelect
              label="Requires historic"
              value={fields.requiresHistoric}
              onChange={(requiresHistoric) =>
                patch("requiresHistoric", requiresHistoric)
              }
            />
            <TriStateSelect
              label="Requires homeownership"
              value={fields.requiresHomeownership}
              onChange={(requiresHomeownership) =>
                patch("requiresHomeownership", requiresHomeownership)
              }
            />
            <TriStateSelect
              label="Requires barn"
              value={fields.requiresBarn}
              onChange={(requiresBarn) => patch("requiresBarn", requiresBarn)}
            />
            <TriStateSelect
              label="Requires income qualification"
              value={fields.requiresIncomeQualification}
              onChange={(requiresIncomeQualification) =>
                patch("requiresIncomeQualification", requiresIncomeQualification)
              }
            />
            <TriStateSelect
              label="Incentive stacking allowed"
              value={fields.incentiveStackingAllowed}
              onChange={(incentiveStackingAllowed) =>
                patch("incentiveStackingAllowed", incentiveStackingAllowed)
              }
            />
            <TriStateSelect
              label="Underserved communities priority"
              value={fields.underservedCommunitiesPriority}
              onChange={(underservedCommunitiesPriority) =>
                patch(
                  "underservedCommunitiesPriority",
                  underservedCommunitiesPriority
                )
              }
            />
          </div>
        </Section>

        <Section title="Program meta">
          <TagListInput
            label="Screening program IDs"
            values={fields.screeningProgramIds}
            onChange={(screeningProgramIds) =>
              patch("screeningProgramIds", screeningProgramIds)
            }
            placeholder="e.g. nyserda-ameep"
          />
          <TagListInput
            label="Eligible project types"
            values={fields.eligibleProjectTypes}
            onChange={(eligibleProjectTypes) =>
              patch("eligibleProjectTypes", eligibleProjectTypes)
            }
            placeholder="e.g. Retrofit"
          />
          <TagListInput
            label="Eligible technologies"
            values={fields.eligibleTechnologies}
            onChange={(eligibleTechnologies) =>
              patch("eligibleTechnologies", eligibleTechnologies)
            }
            placeholder="e.g. Solar photovoltaic (PV)"
          />
        </Section>

        <Section title="Legacy ESPM property types (compatibility)">
          <p className="text-xs text-gray-500">
            Keep these existing category and detail values for compatibility with older
            records. New matching rules should use Building use above.
          </p>
          <NameChecklist
            legend="Eligible property type categories"
            names={categoryNames}
            selected={fields.eligiblePropertyTypeCategories}
            onChange={(eligiblePropertyTypeCategories) =>
              patch(
                "eligiblePropertyTypeCategories",
                eligiblePropertyTypeCategories
              )
            }
            columns={3}
          />
          <NameChecklist
            legend="Eligible property type details"
            names={detailOptions.map((option) => option.name)}
            labels={Object.fromEntries(
              detailOptions.map((option) => [
                option.name,
                `${option.name} (${option.categoryName})`,
              ])
            )}
            selected={fields.eligiblePropertyTypeDetails}
            onChange={(eligiblePropertyTypeDetails) =>
              patch("eligiblePropertyTypeDetails", eligiblePropertyTypeDetails)
            }
            searchable
          />
          <NameChecklist
            legend="Excluded property type categories"
            names={categoryNames}
            selected={fields.excludedPropertyTypeCategories}
            onChange={(excludedPropertyTypeCategories) =>
              patch(
                "excludedPropertyTypeCategories",
                excludedPropertyTypeCategories
              )
            }
            columns={3}
          />
          <NameChecklist
            legend="Excluded property type details"
            names={detailOptions.map((option) => option.name)}
            labels={Object.fromEntries(
              detailOptions.map((option) => [
                option.name,
                `${option.name} (${option.categoryName})`,
              ])
            )}
            selected={fields.excludedPropertyTypeDetails}
            onChange={(excludedPropertyTypeDetails) =>
              patch("excludedPropertyTypeDetails", excludedPropertyTypeDetails)
            }
            searchable
          />
        </Section>
      </fieldset>
    </div>
  );
};

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3 rounded border border-gray-200 bg-white p-3">
      <div className="text-xs font-semibold uppercase text-gray-500">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function TriStateSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (next: boolean | null) => void;
}) {
  const encoded =
    value === true ? "true" : value === false ? "false" : "null";
  return (
    <label className="block text-xs text-gray-600">
      <span className="mb-1 block">{label}</span>
      <select
        value={encoded}
        onChange={(event) => {
          const next = event.target.value;
          if (next === "true") {
            onChange(true);
          } else if (next === "false") {
            onChange(false);
          } else {
            onChange(null);
          }
        }}
        className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
      >
        <option value="null">Not set</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </label>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  integer = false,
}: {
  label: string;
  value: number | null;
  onChange: (next: number | null) => void;
  integer?: boolean;
}) {
  return (
    <label className="block text-xs text-gray-600">
      <span className="mb-1 block">{label}</span>
      <input
        type="number"
        step={integer ? 1 : "any"}
        value={value ?? ""}
        onChange={(event) => {
          const raw = event.target.value.trim();
          if (!raw) {
            onChange(null);
            return;
          }
          const parsed = integer
            ? Number.parseInt(raw, 10)
            : Number.parseFloat(raw);
          onChange(Number.isFinite(parsed) ? parsed : null);
        }}
        className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
      />
    </label>
  );
}

function ProviderSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | null;
  options: string[];
  onChange: (next: string | null) => void;
}) {
  const known = value
    ? options.some((option) => option.toLowerCase() === value.toLowerCase())
    : true;
  const selectValue = !value ? "" : known ? value : "__custom__";

  return (
    <div className="space-y-2">
      <label className="block text-xs text-gray-600">
        <span className="mb-1 block">{label}</span>
        <select
          value={selectValue}
          onChange={(event) => {
            const next = event.target.value;
            if (!next) {
              onChange(null);
              return;
            }
            if (next === "__custom__") {
              onChange(value && !known ? value : "");
              return;
            }
            onChange(next);
          }}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
        >
          <option value="">Not set</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
          <option value="__custom__">Custom…</option>
        </select>
      </label>
      {selectValue === "__custom__" && (
        <input
          type="text"
          value={value ?? ""}
          onChange={(event) => {
            const trimmed = event.target.value.trim();
            onChange(trimmed ? event.target.value : null);
          }}
          placeholder="Custom provider name"
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
        />
      )}
    </div>
  );
}

function TagListInput({
  label,
  values,
  onChange,
  placeholder,
}: {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      return;
    }
    if (values.some((item) => item.toLowerCase() === trimmed.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...values, trimmed]);
    setDraft("");
  };

  return (
    <div>
      <div className="mb-1 block text-xs text-gray-600">{label}</div>
      {values.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {values.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() =>
                onChange(
                  values.filter(
                    (item) => item.toLowerCase() !== value.toLowerCase()
                  )
                )
              }
              className="rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs text-gray-700"
            >
              {value} ×
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
        />
        <button
          type="button"
          onClick={add}
          className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
        >
          Add
        </button>
      </div>
    </div>
  );
}

function NameChecklist({
  legend,
  names,
  labels,
  selected,
  onChange,
  columns = 1,
  searchable = false,
  allowCustom = false,
  customLabel = "Custom value",
  unknownHint = "custom",
}: {
  legend: string;
  names: string[];
  labels?: Record<string, string>;
  selected: string[];
  onChange: (next: string[]) => void;
  columns?: number;
  searchable?: boolean;
  allowCustom?: boolean;
  customLabel?: string;
  /** Shown on a selected value the list does not offer. */
  unknownHint?: string;
}) {
  const [query, setQuery] = useState("");
  const [customDraft, setCustomDraft] = useState("");
  const selectedLookup = useMemo(
    () => new Set(selected.map((name) => name.toLowerCase())),
    [selected]
  );
  const knownLookup = useMemo(
    () => new Set(names.map((name) => name.toLowerCase())),
    [names]
  );
  const unknown = selected.filter((name) => !knownLookup.has(name.toLowerCase()));
  const filteredNames = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return names;
    }
    return names.filter((name) => {
      const label = labels?.[name] ?? name;
      return (
        name.toLowerCase().includes(q) || label.toLowerCase().includes(q)
      );
    });
  }, [labels, names, query]);

  const toggle = (name: string, checked: boolean) => {
    if (checked) {
      if (selectedLookup.has(name.toLowerCase())) {
        return;
      }
      onChange([...selected, name]);
      return;
    }
    onChange(
      selected.filter((item) => item.toLowerCase() !== name.toLowerCase())
    );
  };

  const addCustom = () => {
    const trimmed = customDraft.trim();
    if (!trimmed) {
      return;
    }
    if (selectedLookup.has(trimmed.toLowerCase())) {
      setCustomDraft("");
      return;
    }
    onChange([...selected, trimmed]);
    setCustomDraft("");
  };

  const gridClass =
    columns === 3
      ? "grid grid-cols-1 gap-1 sm:grid-cols-2 xl:grid-cols-3"
      : "grid grid-cols-1 gap-1";

  return (
    <fieldset>
      <legend className="block text-xs text-gray-600 mb-1">{legend}</legend>
      {searchable && (
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Search ${legend.toLowerCase()}`}
          aria-label={`Search ${legend.toLowerCase()}`}
          className="mb-2 w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
        />
      )}
      {unknown.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {unknown.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => toggle(name, false)}
              className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-900"
              title={`${name} — ${unknownHint}`}
            >
              {name} ({unknownHint}) ×
            </button>
          ))}
        </div>
      )}
      <div className="max-h-48 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-2">
        {filteredNames.length === 0 ? (
          <p className="text-xs text-gray-500">No matching options.</p>
        ) : (
          <div className={gridClass}>
            {filteredNames.map((name) => (
              <label
                key={name}
                className="flex items-start gap-2 text-xs text-gray-700"
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={selectedLookup.has(name.toLowerCase())}
                  onChange={(event) => toggle(name, event.target.checked)}
                />
                <span>{labels?.[name] ?? name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      {allowCustom && (
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={customDraft}
            onChange={(event) => setCustomDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addCustom();
              }
            }}
            placeholder={customLabel}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
          />
          <button
            type="button"
            onClick={addCustom}
            className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
          >
            Add
          </button>
        </div>
      )}
    </fieldset>
  );
}

function isInvalidJson(json: string | null | undefined): boolean {
  if (json == null || !json.trim()) {
    return false;
  }
  try {
    JSON.parse(json);
    return false;
  } catch {
    return true;
  }
}
